"use client";

import { ArrowLeft, LayoutGrid, PanelRight, PictureInPicture2, Share2, Sparkles, X } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { Button } from "@/modern/components/ui/button-shadcn";
import { cn } from "@/modern/utils";
import { useAnalyzeFileContext, type Persona } from "../context/AnalyzeFileContext";
import { SHARE_PERMISSIONS_TOOLTIP } from "../analyzeConstants";
import { AdvisorChip } from "./PersonaAvatar";

type AnalyzeUnifiedHeaderProps = {
  personas: Persona[];
  insightCountByPersona: Map<string, number>;
  feedFilter: string | null;
  onFilterChange: (personaId: string | null) => void;
  isLocked: boolean;
  chipSize: "md" | "lg";
  onClose?: () => void;
  title: string;
  subtitle?: string;
  totalInsights: number;
  isDetached?: boolean;
  onToggleDetach?: () => void;
  onOpenSharePermissions?: () => void;
  /** Docked in File Detail soft card — match Details/Promote chrome. */
  embeddedInShell?: boolean;
};

export const AnalyzeUnifiedHeader = ({
  personas,
  insightCountByPersona,
  feedFilter,
  onFilterChange,
  isLocked,
  chipSize,
  onClose,
  title,
  subtitle,
  totalInsights,
  isDetached,
  onToggleDetach,
  onOpenSharePermissions,
  embeddedInShell,
}: AnalyzeUnifiedHeaderProps) => {
  const { typingPersonas } = useAnalyzeFileContext();
  const columnCount = personas.length + 1;
  const avatarDim = chipSize === "lg" ? "w-12 h-12" : "w-11 h-11";
  const gridIconDim = chipSize === "lg" ? "w-5 h-5" : "w-[1.125rem] h-[1.125rem]";

  return (
    <div
      className={cn(
        "shrink-0",
        embeddedInShell
          ? "border-b border-border/40 bg-transparent"
          : "border-b border-border bg-card",
      )}
    >
      <div
        className={cn(
          "flex items-center gap-1.5",
          embeddedInShell ? "px-0 py-0 pb-2.5" : "px-3 py-1.5",
          onToggleDetach && "analyze-float-drag-handle cursor-grab active:cursor-grabbing",
        )}
      >
        {onClose && (embeddedInShell || isDetached) ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className={cn("shrink-0 pointer-events-auto", embeddedInShell && "size-9 rounded-full")}
            onClick={onClose}
            aria-label="Back to details"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
        ) : (
          <Sparkles className="size-4 shrink-0 text-primary pointer-events-none" />
        )}
        <div className="flex min-w-0 flex-1 items-baseline gap-1.5 pointer-events-none">
          <h2
            className={cn(
              "leading-none text-foreground",
              embeddedInShell ? "text-xl font-normal tracking-tight" : "text-sm font-semibold",
            )}
          >
            {title}
          </h2>
          {totalInsights > 0 && (
            <span className="text-[11px] tabular-nums leading-none text-muted-foreground">
              · {totalInsights} insight{totalInsights !== 1 ? "s" : ""}
            </span>
          )}
          {subtitle && (
            <span className="min-w-0 truncate text-[11px] leading-none text-muted-foreground">
              · {subtitle}
            </span>
          )}
        </div>
        <div className="pointer-events-auto flex shrink-0 items-center gap-0.5">
          {onOpenSharePermissions && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className={cn(embeddedInShell && "size-9 rounded-full")}
                  onClick={onOpenSharePermissions}
                  aria-label="Share link and permissions"
                  title={SHARE_PERMISSIONS_TOOLTIP}
                >
                  <Share2 className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="z-[calc(var(--swivu-bryntum-z-index)+30)] max-w-[220px]">
                {SHARE_PERMISSIONS_TOOLTIP}
              </TooltipContent>
            </Tooltip>
          )}
          {onToggleDetach && (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className={cn(embeddedInShell && "size-9 rounded-full")}
              onClick={onToggleDetach}
              aria-label={isDetached ? "Dock to side panel" : "Open focused view"}
              title={isDetached ? "Dock to side panel" : "Open focused view"}
            >
              {isDetached ? <PanelRight className="w-4 h-4" /> : <PictureInPicture2 className="w-4 h-4" />}
            </Button>
          )}
          {onClose && !embeddedInShell && !isDetached ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={onClose}
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </Button>
          ) : null}
        </div>
      </div>

      <div
        className={cn("grid gap-0.5 pb-2", embeddedInShell ? "px-0" : "px-2")}
        style={{
          gridTemplateColumns: isDetached
            ? `repeat(${columnCount}, minmax(7.25rem, 1fr))`
            : `repeat(${columnCount}, minmax(0, 1fr))`,
        }}
      >
        <button
          type="button"
          onClick={() => onFilterChange(null)}
          disabled={isLocked}
          className={cn(
            "flex w-full min-w-0 flex-col items-center gap-1 rounded-lg py-1.5 transition-colors",
            feedFilter === null ? "bg-muted" : "hover:bg-muted/50",
          )}
        >
          <div className="relative">
            <div
              className={cn(
                "relative rounded-full bg-primary/15",
                avatarDim,
                feedFilter === null && "shadow-[0_0_0_3px_hsl(var(--primary)/0.35)]",
              )}
            >
              <div
                className={cn(
                  "absolute inset-[3px] flex items-center justify-center rounded-full bg-card",
                  "ring-2 ring-offset-2 ring-offset-card ring-primary/70",
                  feedFilter === null && "ring-primary",
                )}
              >
                <LayoutGrid
                  className={cn(gridIconDim, feedFilter === null ? "text-primary" : "text-foreground/80")}
                />
              </div>
            </div>
          </div>
          <span
            className={cn(
              "font-medium leading-snug",
              chipSize === "lg" ? "text-sm" : "text-xs",
              feedFilter === null ? "text-foreground font-semibold" : "text-foreground/75",
            )}
          >
            All
          </span>
        </button>

        {personas.map((persona) => (
          <AdvisorChip
            key={persona.id}
            persona={persona}
            insightCount={insightCountByPersona.get(persona.id) ?? 0}
            isSelected={feedFilter === persona.id}
            isGenerating={typingPersonas.has(persona.id)}
            isLocked={isLocked}
            onSelect={() => onFilterChange(persona.id)}
            size={chipSize}
            compactLabels={isDetached}
          />
        ))}
      </div>
    </div>
  );
};
